import {
  ADVICE_NAMES,
  InsuranceAdvice,
  PurchaseRecommendation,
} from "Store/advice/interfaces";
import { getCorrectPayment, isChecked } from "Store/advice/utils";

import { formatToLocaleString } from "Formatting/formatNumber";
import { getCorrectAdviceValues } from "Store/advice/productPriceRequestMappers";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";
import { isOneTimePaymentAllowed } from "../../AdviceCards/utils";
import { generateUnavailableMessage } from "../generateUnavailableMessage";

export const generateIntroMessage = (
  isAdvisor: boolean,
  adviceTexts: any,
  lang: string,
  insurance: {
    name: ADVICE_NAMES;
    title: string;
  },
  insuranceAdvice?: InsuranceAdvice,
): { insuranceType: string; message: string } | null => {
  const mustPurchseFromAdvisor = isDefined(insuranceAdvice)
    ? getCorrectPayment(insuranceAdvice).recommendationConfiguration
        .recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR
    : false;
  const shouldGetMessageWithoutAdviceValue =
    isAdvisor || (!isAdvisor && mustPurchseFromAdvisor);

  const adviceIntroMessages = shouldGetMessageWithoutAdviceValue
    ? adviceTexts.adviceIntroMessages.advisor
    : adviceTexts.adviceIntroMessages;
  const insuranceType =
    adviceTexts.adviceIntroMessages.insuranceTypes[insurance.title][lang];

  if (!(insuranceAdvice && isChecked(insuranceAdvice))) {
    const unavailableMessage = generateUnavailableMessage(
      isAdvisor,
      insurance,
      adviceTexts.adviceUnavailableMessages,
      lang,
      insuranceAdvice,
    )?.message;
    if (unavailableMessage) {
      return { insuranceType, message: unavailableMessage };
    }
    return null;
  }

  const message = shouldGetMessageWithoutAdviceValue
    ? getMessageWithoutAdviceValue(
        insurance.name,
        adviceIntroMessages,
        lang,
        insuranceAdvice,
      )
    : getMessageWithAdviceValue(
        insurance.name,
        adviceIntroMessages,
        lang,
        insuranceAdvice,
      );

  return message !== "" ? { insuranceType, message } : null;
};

const getMessageWithAdviceValue = (
  insuranceName: INSURANCE_NAME_VALUE,
  adviceIntroMessages: any,
  lang: string,
  insuranceAdvice: InsuranceAdvice,
): string => {
  const { adviceValue } = getCorrectAdviceValues(
    getCorrectPayment(insuranceAdvice),
  );
  switch (insuranceName) {
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      let weAdviceText: string =
        adviceIntroMessages.disabilityMessage.weAdvice[lang];
      weAdviceText = weAdviceText.replace(
        "{{value}}",
        formatToLocaleString(adviceValue),
      );
      const oneTimePaymentText = isOneTimePaymentAllowed(
        insuranceAdvice.singlePayment,
      )
        ? adviceIntroMessages.disabilityMessage.oneTimePayment[lang]
        : "";
      return weAdviceText.replace("{{oneTimePayment}}", oneTimePaymentText);
    case INSURANCE_NAME_VALUE.DEATH:
      return `${
        adviceIntroMessages.deathMessage.part1[lang]
      }${formatToLocaleString(adviceValue)}${
        adviceIntroMessages.deathMessage.part2[lang]
      }`;
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return `${
        adviceIntroMessages.criticalIllnessMessage.part1[lang]
      }${formatToLocaleString(adviceValue)}${
        adviceIntroMessages.criticalIllnessMessage.part2[lang]
      }`;
    default:
      return "";
  }
};

const getMessageWithoutAdviceValue = (
  insuranceName: INSURANCE_NAME_VALUE,
  advisorMessages: any,
  lang: string,
  insuranceAdvice: InsuranceAdvice,
): string => {
  switch (insuranceName) {
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      const weAdviceText: string =
        advisorMessages.disabilityMessage.weAdvice[lang];
      const oneTimePaymentText = isOneTimePaymentAllowed(
        insuranceAdvice.singlePayment,
      )
        ? advisorMessages.disabilityMessage.oneTimePayment[lang]
        : "";
      return weAdviceText.replace("{{oneTimePayment}}", oneTimePaymentText);
    case INSURANCE_NAME_VALUE.DEATH:
      return advisorMessages.deathMessage[lang];
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return advisorMessages.criticalIllnessMessage[lang];
    default:
      return "";
  }
};
